import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import Minigrid from 'minigrid';

import {
  withGetSummary,
  withGetCount,
  withFilterCategory,
  CHANNEL_TYPES,
  isCompetition,
  ADINSIGHT_LOG_ACTION_TYPE,
  ADINSIGHT_LOG_API_NAME,
  RECOMMENDATION_IDS,
  getChannel,
  hideInContextRecommendation,
  DOWNLOAD_SCOPE,
  RECOMMENDATION_CATEGORIES,
} from '@bingads-webui-campaign/recommendation-core';
import { DownloadToolbarButton } from '@bingads-webui-campaign-react/recommendation-visuals';
import { OverridableToolbarView } from '@bingads-webui/toolbar';
import {
  CampaignAdgroupSelectorFilterAgent,
  EmptyFilterView,
} from '@bingads-webui-campaign-react/campaign-adgroup-selector-filter';
import { FeedbackModal } from '@bingads-webui-campaign-react/recommendation-feedback-modal';
import { HelpPopup } from '@bingads-webui-react/help-popup';

import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';

import { SummaryCardList } from './summary-card-list';
import { EmptyView } from './empty-view';

class summaryView extends React.PureComponent {
  static propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    dismissedData: PropTypes.objectOf(PropTypes.any),
    reload: PropTypes.func.isRequired,
    viewDetails: PropTypes.func.isRequired,
    viewSummary: PropTypes.func.isRequired,
    viewCategory: PropTypes.func,
    deps: PropTypes.shape({
      i18n: PropTypes.shape({
        getString: PropTypes.func.isRequired,
      }).isRequired,
      locationMap: PropTypes.func.isRequired,
      constants: PropTypes.shape({
        imagePath: PropTypes.string.isRequired,
      }).isRequired,
      permissions: PropTypes.object.isRequired,
      combineText: PropTypes.func,
      adDisplayUrlService: PropTypes.object,
      odata: PropTypes.PropTypes.shape({
        get: PropTypes.func.isRequired,
      }),
      preferencesService: PropTypes.objectOf(PropTypes.any),
      appConfig: PropTypes.objectOf(PropTypes.any).isRequired,
    }).isRequired,
    channel: PropTypes.string.isRequired,
    campaignType: PropTypes.number,
    context: PropTypes.shape({
      Source: PropTypes.string,
      Level: PropTypes.string.isRequired,
      Category: PropTypes.string,
    }).isRequired,
    currency: PropTypes.string.isRequired,
    filterConfig: PropTypes.objectOf(PropTypes.any),
    filterService: PropTypes.objectOf(PropTypes.any),
    campaignAdGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasFilter: PropTypes.bool.isRequired,
    scope: PropTypes.shape({
      levelAt: PropTypes.string.isRequired,
      campaignId: PropTypes.string,
      customerId: PropTypes.string,
      accountId: PropTypes.string,
      adGroupId: PropTypes.string,
    }).isRequired,
    currentActivity: PropTypes.objectOf(PropTypes.any).isRequired,
    readOnly: PropTypes.bool,
    downloadService: PropTypes.objectOf(PropTypes.any),
    guid: PropTypes.string, // for adinsight view event log in aria
    isInlineView: PropTypes.bool,
    hasDataCallback: PropTypes.func,
    hideCardCallback: PropTypes.func,
    initialToolbarConfig: PropTypes.objectOf(PropTypes.any),
    applyAllCallback: PropTypes.func,
    toolbar: PropTypes.element,
    isMCC: PropTypes.bool,
    overallOptimizationScoreBar: PropTypes.shape({
      Gained: PropTypes.number,
      Dismissed: PropTypes.number,
      Available: PropTypes.number,
    }),
    isOptimizationScoreOn: PropTypes.bool,
    newI18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func,
    }),
    isDismissedTabEnabled: PropTypes.bool,
    isFromSummary: PropTypes.bool,
    isAutoApplyRecommendationEnabled: PropTypes.bool,
  };

  static defaultProps = {
    campaignType: null,
    filterService: null,
    filterConfig: null,
    readOnly: false,
    downloadService: null,
    guid: null,
    isInlineView: false,
    hasDataCallback: _.noop,
    hideCardCallback: _.noop,
    initialToolbarConfig: null,
    applyAllCallback: _.noop,
    toolbar: null,
    isMCC: false,
    overallOptimizationScoreBar: null,
    isOptimizationScoreOn: false,
    newI18n: {
      getString: _.noop,
    },
    dismissedData: {},
    viewCategory: _.noop,
    isDismissedTabEnabled: false,
    isFromSummary: false,
    isAutoApplyRecommendationEnabled: false,
  };

  constructor(props) {
    super(props);
    this.upperToolBarInstance = new OverridableToolbarView({
      items: [{
        type: 'dropdown',
        id: 'filter-menu',
      }],
    });

    this.state = {
      modal: {
        isOpen: false,
        isDismissAll: false,
      },
      toolbarConfig: props.initialToolbarConfig,
      hideToolbar: false,
      appliedFilter: undefined,
    };
    if (props.filterService) {
      props.filterService.on('filter-state-changed', this.onFilterStateChanged);
    }
  }

  componentDidMount() {
    this.upperToolBarInstance.render();
    this.initializeMiniGrid();
  }

  componentDidUpdate() {
    this.initializeMiniGrid();
  }

  componentWillUnmount() {
    this.upperToolBarInstance.remove();
  }

  onFilterStateChanged = () => {
    this.props.reload();
  }

  initializeMiniGrid() {
    if (!_.isEmpty(this.props.data) && !this.props.isInlineView) {
      const ops = {
        container: '.recommendation-summary-cards',
        item: '.recommendation-summary-card',
        gutter: 20,
      };
      const autoApplyOps = {
        container: '.auto-apply-recommendation-container .recommendation-summary-cards',
        item: '.recommendation-summary-card',
        gutter: 20,
      };
      const grid = new Minigrid(ops);
      grid.mount();

      if (this.props.isAutoApplyRecommendationEnabled) {
        const autoApplyGrid = new Minigrid(autoApplyOps);
        autoApplyGrid.mount();
      }
    }
  }

  handleDownload = (type, version) => {
    const self = this;
    self.props.downloadService.download({
      channel: self.props.channel,
      campaignType: self.props.campaignType,
      filterService: self.props.filterService,
      recommendationType: type,
    }).then(() => {
      self.logOpportunityEvent({
        type,
        action: ADINSIGHT_LOG_ACTION_TYPE.DOWNLOAD,
        context: _.extend({
          Version: version,
          DownloadScope: DOWNLOAD_SCOPE.INDIVIDUAL,
        }, self.props.context),
      });
    });
  };

  handleFeedbackModalOpen = ({
    recommendationDescription,
    isProvideFeedbackOnly,
    isDismissAll,
    type,
    applyOpportunitiesLevel,
    context,
  }) => {
    this.setState({
      modal: {
        isOpen: true,
        recommendationDescription,
        isProvideFeedbackOnly,
        isDismissAll,
        data: {
          type,
          campaignAdGroups: this.props.campaignAdGroups,
          channel: getChannel(type, this.props.isInlineView, this.props.isMCC),
          applyOpportunitiesLevel,
        },
        context,
      },
    });
  }

  handleHideCard = (type, levelAt) => {
    let id = this.props.scope.accountId;

    if (levelAt === levelAtConstant.CAMPAIGN) {
      id = this.props.scope.campaignId;
    } else if (levelAt === levelAtConstant.ADGROUP) {
      id = this.props.scope.adGroupId;
    }
    hideInContextRecommendation({
      levelAt,
      id,
      type,
      preferencesService: this.props.deps.preferencesService,
      currentActivity: this.props.currentActivity,
      channel: getChannel(type, this.props.isInlineView, this.props.isMCC),
      campaignId: this.props.scope.campaignId,
      context: _.extend({ HideLevel: levelAt }, this.props.context),
      isCardView: true,
      guid: this.props.guid || '',
    });
    this.props.hideCardCallback();
  };

  handleFeedbackSubmit = (option) => {
    this.setState({ modal: { isOpen: false } }, () => {
      this.props.reload({ isFromApplyOrDismiss: option && option.isDismissAll });
    });
  };

  handleFeedbackCancel = () => {
    this.setState({ modal: { isOpen: false } });
  }

  viewAccountRecommendations = () => {
    this.props.viewSummary({
      scope: levelAtConstant.ACCOUNT,
    });
  }

  viewDismissedRecommendations = () => {
    this.props.viewCategory(RECOMMENDATION_CATEGORIES.DISMISSED)();
  }

  logOpportunityEvent = ({
    type = null,
    action,
    position = null,
    input = null,
    context = null,
  }) => {
    let api = ADINSIGHT_LOG_API_NAME.RECOMMENDATION;

    if (isCompetition(type)) {
      api = ADINSIGHT_LOG_API_NAME.COMPETITION;
    }

    this.props.currentActivity.trace({
      type: type || '',
      typeId: RECOMMENDATION_IDS[type],
      action,
      input,
      campaignId: this.props.scope.campaignId,
      channel: getChannel(type, this.props.isInlineView, this.props.isMCC),
      position,
      guid: this.props.guid || '',
      context: JSON.stringify(context),
    }, api);
  }

  updateToolbar = (values) => {
    _.each(_.isArray(values) ? values : [values], (value) => {
      const { id } = value;

      if (this.state.toolbarConfig.has(id)) {
        this.state.toolbarConfig.set(id, _.extend(this.state.toolbarConfig.get(id), value));
        this.setState({
          toolbarConfig: this.state.toolbarConfig,
        });
        if (id === 'predicate-list' && !_.isEqual(this.state.appliedFilter, this.props.filterService.editableFilter)) {
          this.setState({
            appliedFilter: this.props.filterService.editableFilter,
          });
          this.forceUpdate();
        }
      }
    });
  }

  resetToolbarVisibility = ({ forceHideToolbar }) => {
    this.setState({
      hideToolbar: forceHideToolbar,
    });
  }

  render = () => {
    const {
      deps: {
        i18n,
        locationMap,
        constants,
        permissions,
        combineText,
        adDisplayUrlService,
        odata,
        preferencesService,
      },
      data,
      scope,
      channel,
      downloadService,
      hasDataCallback,
      overallOptimizationScoreBar,
      isOptimizationScoreOn,
      dismissedData,
      newI18n,
      isDismissedTabEnabled,
      context,
      isAutoApplyRecommendationEnabled,
    } = this.props;

    let summaryCards = null;
    let autoApplySummaryCards = null;
    let autoApplyHeader = null;
    let emptyView = null;
    let modal = null;
    let toolbar = _.identity;

    const isDataEmpty = _.isEmpty(data);
    const isDismissedDataEmpty = _.isEmpty(dismissedData);

    let summaryCardDataOptOut;
    let summaryCardDataOptIn;

    hasDataCallback(!isDataEmpty);
    // hide if filter objects weren't passed in
    // hide toolbar when there is no data and there is no filter
    if (!isDataEmpty || this.props.hasFilter) {
      const downloadButton = (<DownloadToolbarButton
        key="button-download"
        i18n={i18n}
        disabled={isDataEmpty}
        logOpportunityEvent={this.logOpportunityEvent}
        data={data}
        context={context}
        {..._.pick(this.props, 'channel', 'campaignType', 'downloadService', 'filterService')}
      />);

      if (this.props.initialToolbarConfig) {
        this.updateToolbar({
          id: 'button-download',
          content: downloadButton,
        });

        const ToolbarFilterbarSet = this.props.toolbar;
        toolbar = (<ToolbarFilterbarSet
          hideToolbar={this.state.hideToolbar}
          toolbarConfig={this.state.toolbarConfig}
          updateToolbar={this.updateToolbar}
          resetToolbarVisibility={this.resetToolbarVisibility}
        />);
      } else {
        const toolbarItemDownload = downloadButton;
        let toolbarItemFilter = null;

        if (this.props.filterConfig) {
          toolbarItemFilter = (<CampaignAdgroupSelectorFilterAgent
            key="button-filter"
            filterConfig={this.props.filterConfig}
            filterService={this.props.filterService}
            upperToolBar={this.upperToolBarInstance}
            onFilterStateChanged={this.onFilterStateChanged}
          />);
        }

        toolbar = (
          <div role="toolbar" className="toolbar">
            { toolbarItemDownload}
            { toolbarItemFilter}
          </div>);
      }
    }

    const { imagePath } = constants;
    const emptyViewSharedParam = {
      isOptimizationScoreOn,
      overallOptimizationScoreBar,
      isDismissedDataEmpty,
      viewDismissedRecommendations: this.viewDismissedRecommendations,
      isDismissedTabEnabled,
    };

    if (isDataEmpty && this.props.hasFilter) {
      emptyView = <EmptyFilterView i18n={i18n} imagePath={imagePath} />;
    } else if (
      isDataEmpty &&
      (
        this.props.scope.levelAt === levelAtConstant.ACCOUNT ||
        this.props.scope.levelAt === levelAtConstant.CUSTOMER
      )
    ) {
      emptyView = (<EmptyView
        channel={channel}
        i18n={i18n}
        scope={scope}
        {...emptyViewSharedParam}
        newI18n={newI18n}
      />);
    } else if (isDataEmpty) {
      const EmptyViewWithCount = withGetCount(EmptyView);

      emptyView = (<EmptyViewWithCount
        i18n={i18n}
        scope={scope}
        level={levelAtConstant.ACCOUNT}
        viewAccountRecommendations={this.viewAccountRecommendations}
        {..._.pick(this.props, 'dataService', 'el', 'channel', 'campaignType')}
        {...emptyViewSharedParam}
        newI18n={newI18n}
      />);
    } else {
      const summaryCardsProps = _.pick(
        this.props,
        'campaignAdGroups',
        'channel',
        'context',
        'currency',
        'dataService',
        'el',
        'readOnly',
        'reload',
        'scope',
        'summary',
        'viewDetails',
        'viewSummary',
        'currentActivity',
        'guid',
        'perfMarker',
        'logExpInfo',
        'isInlineView',
        'applyAllCallback',
        'showAsyncApplyModal',
        'closeAsyncApplyModal',
        'isMCC',
        'showApplyConfirmModal',
        'overallOptimizationScoreBar',
        'newI18n',
        'isOptimizationScoreOn',
        'isDismissedTabEnabled',
        'classes',
        'isAutoApplyRecommendationEnabled',
        'viewSettings'
      );

      summaryCardDataOptOut = _.omit(this.props.data, rec => rec.isAutoApplyOptIn);
      summaryCardDataOptIn = _.omit(this.props.data, rec => !rec.isAutoApplyOptIn);
      summaryCards = (
        <SummaryCardList
          data={isAutoApplyRecommendationEnabled ? summaryCardDataOptOut : this.props.data}
          onDownload={downloadService && this.handleDownload}
          onFeedbackModalOpen={this.handleFeedbackModalOpen}
          logOpportunityEvent={this.logOpportunityEvent}
          i18n={i18n}
          locationMap={locationMap}
          {...summaryCardsProps}
          permissions={permissions}
          combineText={combineText}
          adDisplayUrlService={adDisplayUrlService}
          odata={odata}
          preferencesService={preferencesService}
          handleHideCard={this.handleHideCard}
          appConfig={this.props.deps.appConfig}
          isFromDismissTab={_.result(context, 'Category', '') === RECOMMENDATION_CATEGORIES.DISMISSED}
          isFromSummary={this.props.isFromSummary}
          initializeMiniGrid={() => { this.initializeMiniGrid(); }}
        />);
      autoApplyHeader = isAutoApplyRecommendationEnabled && !_.isEmpty(summaryCardDataOptIn) ? (
        <React.Fragment>
          <div className={`${!_.isEmpty(summaryCardDataOptOut) ? 'split-line' : ''}`} />
          <div className="auto-apply-summary-header">{newI18n.getString(_TL_('Scheduled to auto-apply'))}</div>
          <HelpPopup topic="pop_BA_Recommendations_ScheduledAAR" />
        </React.Fragment>
      ) : null;
      autoApplySummaryCards = isAutoApplyRecommendationEnabled ? (
        <div className="auto-apply-recommendation-container">
          <SummaryCardList
            data={summaryCardDataOptIn}
            onDownload={downloadService && this.handleDownload}
            onFeedbackModalOpen={this.handleFeedbackModalOpen}
            logOpportunityEvent={this.logOpportunityEvent}
            i18n={i18n}
            locationMap={locationMap}
            {...summaryCardsProps}
            permissions={permissions}
            combineText={combineText}
            adDisplayUrlService={adDisplayUrlService}
            odata={odata}
            preferencesService={preferencesService}
            handleHideCard={this.handleHideCard}
            appConfig={this.props.deps.appConfig}
            isFromDismissTab={_.result(context, 'Category', '') === RECOMMENDATION_CATEGORIES.DISMISSED}
            isFromSummary={this.props.isFromSummary}
            initializeMiniGrid={() => { this.initializeMiniGrid(); }}
            isAARSection
          />
        </div>
      ) : null;
      modal = (
        <FeedbackModal
          {..._.pick(this.props, 'context', 'dataService', 'el', 'readOnly', 'guid', 'showAlertHandler', 'viewDetails')}
          {...this.state.modal}
          i18n={i18n}
          newI18n={newI18n}
          onCancel={this.handleFeedbackCancel}
          callback={this.handleFeedbackSubmit}
          logOpportunityEvent={this.logOpportunityEvent}
          overallOptimizationScoreBar={overallOptimizationScoreBar}
          permissions={permissions}
          allRecommendationTypes={Object.keys(data)}
          recommendationTypesAvailable={Object.keys(data)}
          useNavigateModal
          isFromSummary={this.props.isFromSummary}
        />);
    }

    return (
      <div className="recommendation-summary-view">
        {channel === CHANNEL_TYPES.COMPETITION ? toolbar : null}
        {(!isAutoApplyRecommendationEnabled || !_.isEmpty(summaryCardDataOptOut)) && summaryCards}
        {autoApplyHeader}
        {autoApplySummaryCards}
        {modal}
        {!this.props.isInlineView && emptyView}
      </div>
    );
  }
}

export const FilteredSummaryView =
  withFilterCategory(summaryView);

export const SummaryView =
  withGetSummary(/* withDetails */ false)(summaryView);
